import { useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useUnleashContext } from '@unleash/proxy-client-react';

import { selectUser } from 'store';

import { useAppSelector } from 'hooks';
import { markUnleashFlagsSynced } from 'hooks/useUnleashFlagsSynced';

export const useUnleashContextHook = () => {
  const updateContext = useUnleashContext();

  const { userId } = useAppSelector(selectUser);

  useEffect(() => {
    if (!userId) {
      datadogRum.clearUser();
      return;
    }

    let isCurrent = true;

    // From the moment this settles, a flag rolled out by patient reads this patient's value
    // (hooks/useUnleashFlagsSynced). In the installed client (unleash-proxy-client, updateContext
    // and updateToggles) it settles after a fetch for this context when the client has started.
    // When the first fetch failed, it waits for a later one that succeeds; callers cap that wait
    // (AI_ACCESS_SETTLE_MAX_WAIT_MS). When the client has not started, it settles at once without
    // a fetch, which would mark the flags synced too early: this relies on the client being started
    // before a patient id reaches this hook. Today FlagProvider starts it on its first commit, and
    // PersistGate (src/index.tsx) renders the app only after the store rehydrates, a later commit.
    void Promise.resolve(updateContext({ userId }))
      .catch(() => undefined)
      .finally(() => {
        if (isCurrent) {
          markUnleashFlagsSynced(userId);
        }
      });
    datadogRum.setUser({ id: userId });

    return () => {
      isCurrent = false;
    };
  }, [userId, updateContext]);
};
